import React from 'react';
import './otto.css';

// Otto the Pilot Owl — the admin/internal mascot (replaces Turbo).
// ADMIN-ONLY: never render on any supplier-facing surface.
//
// Wardrobe is a SWAP LAYER over one shared owl (same body, eyes, cap,
// poses) — only the outfit changes:
//   · EVERYDAY (vintage aviator): brown flight jacket, fleece collar,
//     white scarf, gold wing pin. Default look on all routine surfaces.
//   · CAPTAIN (dress uniform): navy jacket, shirt-V, tie, gold
//     epaulettes + wing pin. Big moments only (award, Oak, first-login,
//     milestones). Only Celebrating / Waving / Default exist in dress.
// Artwork transcribed from the source of truth (otto-pilot-owl.html,
// otto-two-wardrobes.html). Model rules: round white eye socket (r14)
// with the green pupil MOVING inside it for expression; cap ON for
// on-duty poses, OFF (bare head + ear tufts) for at-rest (Searching,
// All caught up). Themed via CSS variables (otto.css).

// ---- shared owl parts ----
const bodyBrown = <ellipse cx="60" cy="76" rx="34" ry="40" fill="var(--brown)" />;

const feet = (
  <>
    <g strokeLinecap="round"><circle cx="52" cy="114" r="3.4" fill="var(--amber)" /><path d="M52 114 L46 125 M52 114 L52 127 M52 114 L58 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
    <g strokeLinecap="round"><circle cx="68" cy="114" r="3.4" fill="var(--amber)" /><path d="M68 114 L62 125 M68 114 L68 127 M68 114 L74 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
  </>
);

const capOn = (
  <>
    <path d="M30 54 a30 26 0 0 1 60 0 q-30 -12 -60 0 z" fill="var(--cap)" />
    <path d="M31 52 q-5 10 -1 20 q7 2 9 -3 q-4 -9 -2 -18 z" fill="var(--cap)" />
    <path d="M89 52 q5 10 1 20 q-7 2 -9 -3 q4 -9 2 -18 z" fill="var(--cap)" />
    <circle cx="47" cy="50" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
    <circle cx="73" cy="50" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
    <path d="M54 50 h12" stroke="#2c2c22" strokeWidth="3" />
  </>
);

const earTufts = (
  <>
    <path d="M42 42 l3 12 l7 -6 z" fill="var(--brown-d)" />
    <path d="M78 42 l-3 12 l-7 -6 z" fill="var(--brown-d)" />
  </>
);

// Round-socket eyes (model rule): fixed white r14 at cy66; only the
// green pupil + inner move (dx/dy) to change expression.
const eyes = (dx = 0, dy = 1) => (
  <>
    <circle cx="48" cy="66" r="14" fill="#fbfdf6" /><circle cx="72" cy="66" r="14" fill="#fbfdf6" />
    <circle cx={50 + dx} cy={66 + dy} r="7.5" fill="var(--fusion-green)" /><circle cx={70 + dx} cy={66 + dy} r="7.5" fill="var(--fusion-green)" />
    <circle cx={50 + dx} cy={66 + dy} r="3.8" fill="#17170f" /><circle cx={70 + dx} cy={66 + dy} r="3.8" fill="#17170f" />
    <circle cx={52 + dx} cy={64 + dy} r="1.7" fill="#fff" /><circle cx={72 + dx} cy={64 + dy} r="1.7" fill="#fff" />
  </>
);
const beak = <path d="M60 72 l-5 7 h10 z" fill="var(--amber)" />;

// ---- outfit layers (the swap axis) ----
const everydayJacket = (
  <>
    <path d="M27 78 q0 26 12 38 q21 8 42 0 q12 -12 12 -38 q-33 14 -66 0z" fill="var(--jacket)" />
    <path d="M60 82 v34" stroke="var(--jacket-d)" strokeWidth="2.5" />
    <g transform="translate(60 93)">
      <path d="M-2.6 -4 h5.2 v3.5 q0 3.5 -2.6 5 q-2.6 -1.5 -2.6 -5z" fill="var(--gold)" />
      <path d="M-3 -2 q-6 -1 -10 0 q-4 0 -7 1 q3 .6 6 .6 q-3 .8 -6 1 q3 .5 6 .4 q-3 .9 -5 1.4 q6 .8 12 -1.2 q3 -1 4 -3.2z" fill="var(--gold)" />
      <path d="M3 -2 q6 -1 10 0 q4 0 7 1 q-3 .6 -6 .6 q3 .8 6 1 q-3 .5 -6 .4 q3 .9 5 1.4 q-6 .8 -12 -1.2 q-3 -1 -4 -3.2z" fill="var(--gold)" />
    </g>
    <path d="M34 76 q10 12 26 12 q16 0 26 -12 q-8 -2 -14 4 q-12 8 -24 0 q-6 -6 -14 -4z" fill="var(--fleece)" />
  </>
);
// Scarf settled — short tail hanging naturally to one side (idle/static).
const everydayScarfSettled = (
  <>
    <path d="M40 74 q20 12 40 0 q2 6 -2 9 q-18 10 -36 0 q-4 -3 -2 -9z" fill="var(--scarf)" />
    <path d="M78 80 q14 4 20 16 q-4 5 -10 3 q-8 -6 -12 -13z" fill="var(--scarf)" />
  </>
);
const dressJacket = (
  <>
    <path d="M27 78 q0 26 12 38 q21 8 42 0 q12 -12 12 -38 q-33 14 -66 0z" fill="var(--navy)" />
    <path d="M60 80 l-16 6 l4 10 l12 -8z" fill="var(--navy-l)" />
    <path d="M60 80 l16 6 l-4 10 l-12 -8z" fill="var(--navy-l)" />
    <path d="M60 80 l-8 22 h16z" fill="var(--shirt)" />
    <path d="M60 84 l-4 6 l4 16 l4 -16z" fill="var(--tie)" />
    <path d="M56 90 l4 -6 l4 6 l-4 4z" fill="var(--tie-d)" />
    <rect x="29" y="82" width="13" height="7" rx="2" fill="var(--navy-d)" />
    <path d="M31 84 h9 M31 86 h9 M31 88 h9" stroke="var(--gold)" strokeWidth="1" />
    <rect x="78" y="82" width="13" height="7" rx="2" fill="var(--navy-d)" />
    <path d="M80 84 h9 M80 86 h9 M80 88 h9" stroke="var(--gold)" strokeWidth="1" />
    <g transform="translate(60 104)">
      <path d="M-2.4 -3.5 h4.8 v3 q0 3 -2.4 4.4 q-2.4 -1.4 -2.4 -4.4z" fill="var(--gold)" />
      <path d="M-3 -1.6 q-5 -1 -9 0 q3 .6 5 .8 q-2 .8 -4 1 q5 .8 10 -1z" fill="var(--gold)" />
      <path d="M3 -1.6 q5 -1 9 0 q-3 .6 -5 .8 q2 .8 4 1 q-5 .8 -10 -1z" fill="var(--gold)" />
    </g>
  </>
);
// Torso for the upright poses. Captain uses the dress jacket (tie, no
// scarf); everyday adds the settled scarf.
const torso = (variant) => variant === 'captain'
  ? dressJacket
  : <>{everydayJacket}{everydayScarfSettled}</>;

// ---- pose builder ----
function buildPose(key, variant) {
  switch (key) {
    case 'waving':
      return (
        <>
          {bodyBrown}
          <path d="M92 60 q16 -6 20 -20 q-4 16 -10 26 q-6 6 -12 4 z" fill="var(--brown-d)" />
          {torso(variant)}{feet}{eyes(0, 1)}{beak}{capOn}
        </>
      );
    case 'searching':
      // Bare head (cap off), pupils glancing up, thought dots. Everyday only.
      return (
        <>
          {bodyBrown}{everydayJacket}{everydayScarfSettled}{feet}{earTufts}{eyes(-1, -4)}{beak}
          <circle cx="92" cy="40" r="3" fill="var(--txt-low)" /><circle cx="100" cy="32" r="4" fill="var(--txt-mid)" />
        </>
      );
    case 'writing':
      // Cap on, pupils down to the clipboard; clipboard + pen over the jacket.
      return (
        <>
          {bodyBrown}{everydayJacket}{everydayScarfSettled}{feet}{eyes(0, 4)}{beak}{capOn}
          <g transform="rotate(-8 62 104)">
            <rect x="44" y="94" width="40" height="28" rx="3" fill="#2f3128" stroke="var(--brown-d)" />
            <rect x="58" y="91" width="12" height="6" rx="2" fill="var(--amber)" />
            <path d="M50 103 h26 M50 109 h18" stroke="var(--txt-low)" strokeWidth="2" strokeLinecap="round" />
          </g>
          <path d="M84 84 q10 4 12 14 q-6 2 -12 -2 q-4 -6 0 -12z" fill="var(--brown-d)" />
          <path d="M92 92 l10 12" stroke="var(--amber)" strokeWidth="3.4" strokeLinecap="round" />
          <circle cx="103" cy="105" r="2" fill="var(--fusion-green)" />
        </>
      );
    case 'sleeping':
      // Bare head, closed-eye arcs, wing tucked over the jacket, Zzz.
      return (
        <>
          {bodyBrown}{everydayJacket}{everydayScarfSettled}{feet}{earTufts}
          <path d="M40 64 q8 6 16 0" stroke="#3a2c1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M64 64 q8 6 16 0" stroke="#3a2c1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          {beak}
          <path d="M30 82 q-6 14 6 26 q6 -10 6 -24z" fill="var(--brown-d)" />
          <text x="92" y="46" fontFamily="Metropolis, Manrope, sans-serif" fontWeight="800" fontSize="13" fill="var(--txt-mid)">z</text>
          <text x="100" y="36" fontFamily="Metropolis, Manrope, sans-serif" fontWeight="800" fontSize="16" fill="var(--txt-low)">Z</text>
        </>
      );
    case 'flying':
    case 'flying-empty': {
      // Cap + goggles down, wings swept back, everyday jacket + a scarf
      // that STREAMS back (opposite travel). Carries the brief only when
      // flying (send); flying-empty is the loading hover.
      const carryingBrief = key === 'flying';
      return (
        <>
          <g transform="rotate(-18 60 64)">
            {/* The wings carry the same part classes the roaming shots use, so
                anything that wants him beating them can reuse the existing
                otto-fly-wing keyframes instead of authoring new ones. The
                classes alone animate nothing — every rule is scoped to a
                parent (.otto-roam-fly, .brief-send-otto), so the static
                swept-back pose is unchanged wherever nobody asks for flapping. */}
            <path className="otto-part-lwing" d="M34 66 q-22 6 -34 24 q18 -4 26 -8 q10 -4 14 -10z" fill="var(--brown-d)" />
            <path className="otto-part-rwing" d="M86 66 q22 6 34 24 q-18 -4 -26 -8 q-10 -4 -14 -10z" fill="var(--brown-d)" />
            <ellipse cx="60" cy="66" rx="30" ry="34" fill="var(--brown)" />
            {/* everyday jacket over the lower body */}
            <path d="M33 70 q0 20 11 28 q16 6 32 0 q11 -9 11 -28 q-31 12 -65 0z" fill="var(--jacket)" />
            <path d="M60 74 v24" stroke="var(--jacket-d)" strokeWidth="2" />
            <path d="M36 68 q24 10 48 0 q-6 8 -14 9 q-10 3 -20 0 q-8 -1 -14 -9z" fill="var(--fleece)" />
            {/* scarf streaming BACK (down-left, opposite the up-right travel) */}
            <path d="M40 70 q-20 10 -34 30 q10 1 20 -6 q10 -6 16 -14z" fill="var(--scarf)" />
            <path d="M46 74 q-16 12 -24 28 q9 -1 16 -8 q6 -6 10 -14z" fill="var(--scarf-sh)" opacity="0.7" />
            <path d="M54 98 l-4 9 M60 99 l0 10 M66 98 l4 9" stroke="var(--amber)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            <circle cx="49" cy="58" r="14" fill="#fbfdf6" /><circle cx="71" cy="58" r="14" fill="#fbfdf6" />
            <circle cx="52" cy="55" r="6.5" fill="var(--fusion-green)" /><circle cx="74" cy="55" r="6.5" fill="var(--fusion-green)" />
            <circle cx="53" cy="54" r="3.2" fill="#17170f" /><circle cx="75" cy="54" r="3.2" fill="#17170f" />
            <path d="M60 62 l-5 7 h10 z" fill="var(--amber)" />
            <path d="M30 48 a30 26 0 0 1 60 0 q-30 -12 -60 0 z" fill="var(--cap)" />
            <path d="M31 46 q-5 9 -1 18 q7 2 9 -3 q-4 -8 -2 -16 z" fill="var(--cap)" />
            <path d="M89 46 q5 9 1 18 q-7 2 -9 -3 q4 -8 2 -16 z" fill="var(--cap)" />
            <circle cx="49" cy="56" r="8" fill="#bfe3ea" opacity=".55" stroke="#2c2c22" strokeWidth="3" />
            <circle cx="71" cy="56" r="8" fill="#bfe3ea" opacity=".55" stroke="#2c2c22" strokeWidth="3" />
            <path d="M57 56 h6" stroke="#2c2c22" strokeWidth="3" />
            {carryingBrief && (
              <>
                <g transform="rotate(10 52 92)">
                  <rect x="40" y="84" width="26" height="18" rx="2.5" fill="#fbfdf6" stroke="#cdc7b2" strokeWidth="1.5" />
                  <path d="M40 86 l13 8 l13 -8" fill="none" stroke="#cdc7b2" strokeWidth="1.5" />
                  <rect x="40" y="84" width="26" height="4.5" fill="var(--fusion-green)" opacity=".9" />
                </g>
                <path d="M40 70 q-14 8 -20 22 q12 -1 20 -6 q8 -5 8 -12z" fill="var(--brown)" />
              </>
            )}
          </g>
          <path d="M14 96 q10 -3 18 -1 M10 106 q12 -3 20 -1" stroke="var(--txt-low)" strokeWidth="2" fill="none" strokeLinecap="round" opacity=".6" />
        </>
      );
    }
    case 'celebrating':
      if (variant === 'captain') {
        // Dress uniform celebrating — canonical dress-cheer (navy wings,
        // navy jacket, shirt-V, tie, epaulettes, big happy eyes, smile).
        return (
          <>
            <path d="M32 88 q-14 -2 -24 -12 q-6 -6 -9 -15 q6 5 11 7 q-2 -5 -2 -9 q4 6 8 8 q0 -5 1 -8 q3 7 7 11 q6 6 11 9 q4 2 3 6 q-1 4 -5 3z" fill="var(--navy-d)" />
            <path d="M88 88 q14 -2 24 -12 q6 -6 9 -15 q-6 5 -11 7 q2 -5 2 -9 q-4 6 -8 8 q0 -5 -1 -8 q-3 7 -7 11 q-6 6 -11 9 q-4 2 -3 6 q1 4 5 3z" fill="var(--navy-d)" />
            <ellipse cx="60" cy="78" rx="33" ry="39" fill="var(--brown)" />
            <path d="M28 80 q0 24 12 36 q20 8 40 0 q12 -12 12 -36 q-31 13 -64 0z" fill="var(--navy)" />
            <path d="M60 82 l-8 20 h16z" fill="var(--shirt)" />
            <path d="M60 86 l-4 6 l4 14 l4 -14z" fill="var(--tie)" />
            <rect x="30" y="84" width="12" height="7" rx="2" fill="var(--navy-d)" /><path d="M32 86 h8 M32 88 h8" stroke="var(--gold)" strokeWidth="1" />
            <rect x="78" y="84" width="12" height="7" rx="2" fill="var(--navy-d)" /><path d="M80 86 h8 M80 88 h8" stroke="var(--gold)" strokeWidth="1" />
            <g strokeLinecap="round"><circle cx="52" cy="114" r="3.4" fill="var(--amber)" /><path d="M52 114 L46 125 M52 114 L52 127 M52 114 L58 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
            <g strokeLinecap="round"><circle cx="68" cy="114" r="3.4" fill="var(--amber)" /><path d="M68 114 L62 125 M68 114 L68 127 M68 114 L74 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
            <circle cx="47" cy="68" r="14" fill="#fbfdf6" /><circle cx="73" cy="68" r="14" fill="#fbfdf6" />
            <circle cx="47" cy="68" r="7.5" fill="var(--fusion-green)" /><circle cx="73" cy="68" r="7.5" fill="var(--fusion-green)" />
            <circle cx="47" cy="68" r="3.6" fill="#17170f" /><circle cx="73" cy="68" r="3.6" fill="#17170f" />
            <circle cx="49" cy="66" r="1.7" fill="#fff" /><circle cx="75" cy="66" r="1.7" fill="#fff" />
            <path d="M60 74 l-5 7 h10 z" fill="var(--amber)" />
            <path d="M30 56 a30 26 0 0 1 60 0 q-30 -12 -60 0 z" fill="var(--cap)" />
            <path d="M31 54 q-5 9 -1 18 q7 2 9 -3 q-4 -8 -2 -16 z" fill="var(--cap)" />
            <path d="M89 54 q5 9 1 18 q-7 2 -9 -3 q4 -8 2 -16 z" fill="var(--cap)" />
            <circle cx="48" cy="49" r="6" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="2.6" />
            <circle cx="72" cy="49" r="6" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="2.6" />
            <path d="M54 49 h12" stroke="#2c2c22" strokeWidth="2.6" />
            <circle cx="14" cy="36" r="3" fill="var(--fusion-green)" /><circle cx="106" cy="36" r="3" fill="var(--fusion-green)" />
            <circle cx="24" cy="24" r="2" fill="var(--amber)" /><circle cx="96" cy="24" r="2" fill="var(--amber)" />
          </>
        );
      }
      // Everyday celebrating — brown wings raised, everyday jacket + scarf.
      return (
        <>
          <path d="M32 88 q-14 -2 -24 -12 q-6 -6 -9 -15 q6 5 11 7 q-2 -5 -2 -9 q4 6 8 8 q0 -5 1 -8 q3 7 7 11 q6 6 11 9 q4 2 3 6 q-1 4 -5 3z" fill="var(--brown-d)" />
          <path d="M88 88 q14 -2 24 -12 q6 -6 9 -15 q-6 5 -11 7 q2 -5 2 -9 q-4 6 -8 8 q0 -5 -1 -8 q-3 7 -7 11 q-6 6 -11 9 q-4 2 -3 6 q1 4 5 3z" fill="var(--brown-d)" />
          <ellipse cx="60" cy="78" rx="33" ry="39" fill="var(--brown)" />
          <path d="M28 80 q0 24 12 36 q20 8 40 0 q12 -12 12 -36 q-31 13 -64 0z" fill="var(--jacket)" />
          <path d="M60 84 v32" stroke="var(--jacket-d)" strokeWidth="2.5" />
          <path d="M34 78 q10 11 26 11 q16 0 26 -11 q-8 -2 -14 4 q-12 7 -24 0 q-6 -6 -14 -4z" fill="var(--fleece)" />
          <path d="M40 76 q20 12 40 0 q2 6 -2 9 q-18 10 -36 0 q-4 -3 -2 -9z" fill="var(--scarf)" />
          <path d="M78 82 q14 4 20 16 q-4 5 -10 3 q-8 -6 -12 -13z" fill="var(--scarf)" />
          <g strokeLinecap="round"><circle cx="52" cy="114" r="3.4" fill="var(--amber)" /><path d="M52 114 L46 125 M52 114 L52 127 M52 114 L58 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
          <g strokeLinecap="round"><circle cx="68" cy="114" r="3.4" fill="var(--amber)" /><path d="M68 114 L62 125 M68 114 L68 127 M68 114 L74 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
          <circle cx="47" cy="68" r="14" fill="#fbfdf6" /><circle cx="73" cy="68" r="14" fill="#fbfdf6" />
          <circle cx="47" cy="68" r="7.5" fill="var(--fusion-green)" /><circle cx="73" cy="68" r="7.5" fill="var(--fusion-green)" />
          <circle cx="47" cy="68" r="3.6" fill="#17170f" /><circle cx="73" cy="68" r="3.6" fill="#17170f" />
          <circle cx="49" cy="66" r="1.7" fill="#fff" /><circle cx="75" cy="66" r="1.7" fill="#fff" />
          <path d="M60 74 l-5 7 h10 z" fill="var(--amber)" />
          <path d="M30 56 a30 26 0 0 1 60 0 q-30 -12 -60 0 z" fill="var(--cap)" />
          <path d="M31 54 q-5 9 -1 18 q7 2 9 -3 q-4 -8 -2 -16 z" fill="var(--cap)" />
          <path d="M89 54 q5 9 1 18 q-7 2 -9 -3 q4 -8 2 -16 z" fill="var(--cap)" />
          <circle cx="48" cy="49" r="6" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="2.6" />
          <circle cx="72" cy="49" r="6" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="2.6" />
          <path d="M54 49 h12" stroke="#2c2c22" strokeWidth="2.6" />
          <circle cx="14" cy="36" r="3" fill="var(--fusion-green)" /><circle cx="106" cy="36" r="3" fill="var(--fusion-green)" />
          <circle cx="24" cy="24" r="2" fill="var(--amber)" /><circle cx="96" cy="24" r="2" fill="var(--amber)" />
        </>
      );
    case 'default':
    default:
      return (
        <>
          {bodyBrown}{torso(variant)}{feet}{eyes(0, 1)}{beak}{capOn}
        </>
      );
  }
}

// Friendly aliases so call sites can name the moment, not the pose.
const ALIASES = {
  welcome: 'waving',
  loading: 'flying-empty',
  award: 'celebrating',
  oak: 'celebrating',
  drafting: 'writing',
  sent: 'flying',
  empty: 'sleeping',
  'all-caught-up': 'sleeping',
};

// Dress uniform only exists for these poses (§3). Any other pose falls
// back to everyday even if variant="captain" is passed.
const DRESS_POSES = new Set(['default', 'waving', 'celebrating']);

export default function Otto({
  pose = 'default',
  variant = 'everyday',
  size = 120,
  className = '',
  bob = false,
  ariaLabel = 'Otto, the Supply Pilot mascot',
}) {
  const key = ALIASES[pose] || pose;
  const effVariant = variant === 'captain' && DRESS_POSES.has(key) ? 'captain' : 'everyday';
  const content = buildPose(key, effVariant);
  const decorative = ariaLabel === '';
  const height = Math.round(size * 132 / 120);
  const inner = bob ? <g className="otto-bob">{content}</g> : content;
  return (
    <svg
      className={`otto-svg ${className}`.trim()}
      viewBox="0 0 120 132"
      width={size}
      height={height}
      role={decorative ? 'presentation' : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : ariaLabel}
    >
      {inner}
    </svg>
  );
}
